import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import { CheckCircle, Eye, EyeOff, AlertTriangle } from 'lucide-react';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | valid | invalid | expired | done
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/auth/verify-reset-token/${token}`);
        if (cancelled) return;
        if (res.data.valid) {
          setEmail(res.data.email || '');
          setStatus('valid');
        } else {
          setStatus(res.data.reason === 'expired' ? 'expired' : 'invalid');
        }
      } catch {
        if (!cancelled) setStatus('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setStatus('done');
      toast.success('Password updated. You can now log in.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md bg-white rounded-2xl border border-stone-100 shadow-[0_4px_20px_-2px_rgba(87,83,78,0.1)]" data-testid="reset-card">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-2xl font-bold" style={{ color: '#1a3660' }}>Reset Password</CardTitle>
          {email && <p className="text-sm text-stone-500 mt-1">for {email}</p>}
        </CardHeader>
        <CardContent>
          {status === 'checking' && (
            <p className="text-center text-sm text-stone-500 py-6">Checking your reset link…</p>
          )}

          {(status === 'invalid' || status === 'expired') && (
            <div className="text-center py-4" data-testid="reset-invalid">
              <AlertTriangle className="w-10 h-10 text-orange-500 mx-auto mb-3" />
              <p className="text-stone-700 font-medium mb-2">
                {status === 'expired' ? 'This reset link has expired.' : 'This reset link is invalid or already used.'}
              </p>
              <p className="text-sm text-stone-500 mb-4">Reset links are good for 1 hour and can only be used once.</p>
              <Link to="/login" className="inline-block">
                <Button className="rounded-full bg-orange-600 hover:bg-orange-700 text-white px-6">
                  Back to Log In
                </Button>
              </Link>
            </div>
          )}

          {status === 'valid' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="pw" className="text-stone-700 text-sm font-medium">New password</Label>
                <div className="relative mt-1">
                  <Input
                    id="pw"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    required
                    className="rounded-xl border-stone-200 bg-stone-50 h-12 pr-11"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    aria-label="Toggle password visibility"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="cpw" className="text-stone-700 text-sm font-medium">Confirm password</Label>
                <Input
                  id="cpw"
                  type={showPw ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat your new password"
                  required
                  className="mt-1 rounded-xl border-stone-200 bg-stone-50 h-12"
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-6 h-auto text-base"
                data-testid="reset-submit-btn"
              >
                {saving ? 'Updating…' : 'Update Password'}
              </Button>
            </form>
          )}

          {status === 'done' && (
            <div className="text-center py-4">
              <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-stone-700">Password updated! Redirecting to login…</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
